import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

const PAGE_SIZE = 10;

/** Medios de pago del core (mi_funcion/mediosPago.js) — solo labels. */
export const MEDIOS_PAGO_LABEL = {
  efectivo: 'Efectivo',
  yape: 'Yape',
  plin: 'PLIN',
  transferencia: 'Transferencia',
  pos: 'POS / Tarjeta',
  credito: 'Crédito',
  sin_registro: 'Sin registro',
};

export function mapCaja(snap) {
  return { id: snap.id, ...snap.data() };
}

export async function listCajas({ ruc, pageSize = PAGE_SIZE }) {
  if (!ruc) throw new Error('RUC requerido');
  const snap = await getDocs(
    query(collection(db, 'cajas'), where('ruc', '==', ruc), orderBy('fechaApertura', 'desc'), limit(pageSize))
  );
  return snap.docs.map(mapCaja);
}

export async function getCajaAbierta({ ruc }) {
  if (!ruc) throw new Error('RUC requerido');
  const snap = await getDocs(
    query(collection(db, 'cajas'), where('ruc', '==', ruc), where('estado', '==', 'abierta'), limit(1))
  );
  if (snap.empty) return null;
  return mapCaja(snap.docs[0]);
}

/**
 * Apertura de caja — mismo doc que CajaScreen.
 * El cajero es el usuario autenticado (la lista `users` está denegada por Rules).
 */
export async function openCaja({ ruc, montoInicial, cajeroId, cajeroName }) {
  const monto = parseFloat(montoInicial);
  if (!monto || monto <= 0) throw new Error('El monto inicial debe ser mayor a 0.');

  const abierta = await getCajaAbierta({ ruc });
  if (abierta) {
    throw new Error(`Ya hay una caja abierta (${abierta.cajeroName || abierta.cajeroId}). Ciérrala antes de abrir otra.`);
  }

  return addDoc(collection(db, 'cajas'), {
    ruc,
    cajeroId: cajeroId || '',
    cajeroName: cajeroName || 'Admin web',
    montoInicial: monto,
    fechaApertura: serverTimestamp(),
    estado: 'abierta',
  });
}

/** Arqueo: pedidos pagados vinculados a la caja (mismo criterio que CajaScreen). */
export async function calcularArqueo({ ruc, cajaId }) {
  const snap = await getDocs(
    query(collection(db, 'pedidos'), where('cajaId', '==', cajaId), where('ruc', '==', ruc), limit(200))
  );
  const documentos = [];
  for (const d of snap.docs) {
    const data = d.data();
    if (data.status === 'pagado' || data.facturado) {
      documentos.push({ id: d.id, ...data });
    }
  }

  const agrupado = {};
  for (const doc of documentos) {
    const medio = doc.medioPago || 'sin_registro';
    if (!agrupado[medio]) agrupado[medio] = 0;
    agrupado[medio] += Number(doc.total || 0);
  }

  const ventasEfectivo = agrupado.efectivo || 0;
  const totalVentas = documentos.reduce((s, d) => s + Number(d.total || 0), 0);

  return {
    ventasPorMedio: agrupado,
    ventasEfectivo,
    totalVentas,
    totalDocumentos: documentos.length,
    documentosConComprobante: documentos.filter((d) => d.comprobanteEmitido).length,
    documentos: documentos.slice(0, 20),
  };
}

export async function closeCaja({ ruc, cajaId, montoFinal }) {
  const monto = parseFloat(montoFinal);
  if (Number.isNaN(monto)) throw new Error('Ingrese el monto final.');

  const arqueo = await calcularArqueo({ ruc, cajaId });
  const snap = await getDocs(query(collection(db, 'cajas'), where('ruc', '==', ruc), limit(50)));
  const caja = snap.docs.find((d) => d.id === cajaId);
  if (!caja) throw new Error('Caja no encontrada.');
  const data = caja.data();
  if (data.estado !== 'abierta') throw new Error('Esta caja ya está cerrada.');

  const montoInicial = Number(data.montoInicial || 0);
  const efectivoEsperado = montoInicial + arqueo.ventasEfectivo;
  const sobrante = monto - efectivoEsperado;

  await updateDoc(doc(db, 'cajas', cajaId), {
    montoFinal: monto,
    fechaCierre: serverTimestamp(),
    estado: 'cerrada',
    ventasEfectivo: arqueo.ventasEfectivo,
    totalVentas: arqueo.totalVentas,
    efectivoEsperado,
    sobrante,
    desgloseSnapshot: arqueo.ventasPorMedio,
  });

  return { arqueo, efectivoEsperado, sobrante, montoFinal: monto };
}
